import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  TextField,
  Avatar,
  Icon,
  Label,
  Button,
  Modal,
  ModalButtons,
  ModalContent,
  ProgressBar,
} from 'mw-style-react';
import cn from 'classnames';
import { SHOW_NOTIFY, NOTIFY_LEVEL } from 'constants';
import {
  SEARCH_LAYERS,
  GET_LAYERS,
  CREATE_LAYER,
} from '@control-front-end/common/constants/graphLayers';
import {
  BULK_ADD_TO_GRAPH,
  BULK_REMOVE_FROM_GRAPH,
} from '@control-front-end/common/constants/graphActors';
import AppUtils from '@control-front-end/utils/utils';
import useIntl from 'useIntl';
import mes from './intl';
// eslint-disable-next-line no-unused-vars
import m from '../../Modal.scss'; // NOSONAR
// eslint-disable-next-line no-unused-vars
import l from './ActorsToLayer.scss'; // NOSONAR

function ActorsToLayer(props) {
  const {
    data: { action, layerId, elements, hasLoops },
    visibility,
    onClose,
    isSubmit,
    callback,
  } = props;
  const t = useIntl();
  const searchBox = useRef();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const initState = {
    list: [],
    limit: 15,
    offset: 0,
    endList: false,
    reqStatus: 'success',
  };
  const [layers, setLayers] = useState(initState);
  const [target, setTarget] = useState({});
  const [newLayerMode, setNewLayerMode] = useState(false);

  /**
   * Получение слоев
   */
  const getLayers = (loadMore = false) => {
    dispatch({
      type: GET_LAYERS.REQUEST,
      payload: { loadMore, layersState: layers },
      callback: (data) => {
        const list = data.list.filter((i) => i.isCustom);
        setLayers({ ...layers, ...data, list });
      },
    });
  };

  useEffect(() => {
    getLayers();
  }, []);

  /**
   * Выбор слоя
   */
  const handleSelectLayer = (id, type) => {
    if (id === target.layerId) return;
    setTarget({ layerId: id, name: '', type });
  };

  /**
   * Новый слой
   */
  const handleNewLayer = ({ value }) => {
    setTarget({ layerId: 'new', name: value, type: 'graph' });
  };

  /**
   * Очистка строки поиска слоев
   */
  const clearSearch = () => {
    setQuery('');
    setTarget({});
    setLayers(initState);
    getLayers();
  };

  /**
   * Поиск слоев
   */
  const handleSearch = ({ value: newQuery }) => {
    if (newQuery.length === 0) {
      clearSearch();
      return;
    }
    setQuery(newQuery);
    if (newQuery.length < 3) {
      setTarget({});
      return;
    }
    dispatch({
      type: SEARCH_LAYERS.REQUEST,
      payload: { query: newQuery },
      callback: (data) => {
        const list = data.filter((i) => i.isCustom);
        setLayers({ ...initState, list });
      },
    });
  };

  /**
   * Уведомление об успешном копировании/перемещении
   */
  const notifySuccess = () => {
    dispatch({
      type: SHOW_NOTIFY.REQUEST,
      payload: {
        id: AppUtils.createRid(),
        type: NOTIFY_LEVEL.SUCCESS,
        label: t(mes[`${action}Successfully`]),
      },
    });
    onClose();
  };

  /**
   * Нотификация об отсутствии прав
   */
  const notifyExist = (label) => {
    dispatch({
      type: SHOW_NOTIFY.REQUEST,
      payload: {
        id: AppUtils.createRid(),
        type: NOTIFY_LEVEL.ERROR,
        label,
      },
    });
  };

  /**
   * Переместить на выбранный слой
   */
  const moveToLayer = ({ id: newLayerId, type }) => {
    if (hasLoops && type === 'tree') {
      notifyExist(t(mes.moveLoop));
      return;
    }
    const nodes = [];
    const edges = [];
    elements.forEach((el) => {
      if (el.type === 'edge') {
        edges.push(el);
      } else {
        nodes.push(el);
      }
    });
    dispatch({
      type: BULK_ADD_TO_GRAPH.REQUEST,
      payload: { layerId: newLayerId, nodes, edges },
      callback: (addedNodes) => {
        if (action === 'move') {
          dispatch({
            type: BULK_REMOVE_FROM_GRAPH.REQUEST,
            payload: { list: elements, manageLayer: true },
            callback: () => {
              notifySuccess();
              if (callback) callback(newLayerId, addedNodes);
            },
          });
        } else {
          notifySuccess();
          if (callback) callback(newLayerId, addedNodes);
        }
      },
    });
  };

  /**
   * Подтвержден перенос акторов
   */
  const handleSubmit = () => {
    if (target.layerId === 'new') {
      dispatch({
        type: CREATE_LAYER.REQUEST,
        payload: { name: target.name, isNew: false },
        callback: (layer) => {
          moveToLayer({ id: layer.id, type: 'graph' });
        },
      });
    } else {
      moveToLayer({ id: target.layerId, type: target.type });
    }
  };

  const renderItem = (id, name, type) => {
    const isActive = id === target.layerId;
    return (
      <div
        key={id}
        styleName={cn('l.layerMove__item__wrap', { 'l.active': isActive })}
      >
        <div
          styleName="l.layerMove__item"
          onClick={() => handleSelectLayer(id, type)}
        >
          <div styleName="l.layerMove__item__avatar__box">
            <Avatar styleName="l.layerMove__item__avatar">
              <Icon type="layer" size="small" />
            </Avatar>
          </div>
          <div styleName="l.layerMove__item__info">
            <Label value={name} />
          </div>
          {isActive ? (
            <Icon styleName="l.layerMove__item__icon" type="check" />
          ) : null}
        </div>
      </div>
    );
  };

  const renderNewItem = () => (
    <div styleName="l.layerMove__new">
      <div styleName="l.layerMove__item__avatar__box">
        <Avatar styleName="l.layerMove__item__avatar">
          <Icon type="layer" size="small" />
        </Avatar>
      </div>
      <TextField
        styleName="l.layerMove__new__name"
        autoFocus={true}
        value={target.name || ''}
        placeholder="New layer"
        onChange={handleNewLayer}
      />
    </div>
  );

  return (
    <Modal
      styleName="m.modal l.modal"
      size="small"
      onClose={onClose}
      label={t(mes[`${action}Actors`])}
      visibility={visibility}
    >
      <ModalContent styleName="m.modal__content l.modal__content">
        <div styleName="l.layerMove">
          {newLayerMode ? (
            renderNewItem()
          ) : (
            <>
              <TextField
                styleName="m.textfield l.layerMove__search"
                leftIcon="search"
                value={query}
                bordered={true}
                rightIcon={query.length ? 'close' : ''}
                onClickRightIcon={clearSearch}
                placeholder={t(mes.searchTargetLayer)}
                onChange={handleSearch}
              />
              <div ref={searchBox} styleName="l.layerMove__content">
                {layers.list.length ? (
                  layers.list
                    .filter((layer) => layer.id !== layerId)
                    .map(({ id, name, type }) => renderItem(id, name, type))
                ) : (
                  <div styleName="l.layerMove__content__empty">
                    <Icon type="search" size="xlarge" />
                    <Label value={t(mes.searchTargetNoRes)} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </ModalContent>
      <ModalButtons styleName="m.modal__buttons l.modal__buttons">
        <Button
          label={t(mes.moveToNewLayer)}
          type="quaternary"
          size="smallplus"
          icon="plus"
          onClick={() => setNewLayerMode(true)}
          visibility={newLayerMode ? 'hidden' : 'visible'}
        />
        <Button
          label={t(mes.selectExistLayer)}
          type="quaternary"
          size="smallplus"
          icon="layer"
          onClick={() => setNewLayerMode(false)}
          visibility={newLayerMode ? 'visible' : 'hidden'}
        />
        <Button
          label={t(mes[action])}
          size="smallplus"
          onClick={handleSubmit}
          visibility={target.layerId ? 'visible' : 'disabled'}
        />
        <ProgressBar
          styleName="m.modal__loader"
          type="circle"
          size="small"
          visibility={isSubmit ? 'visible' : 'hidden'}
        />
      </ModalButtons>
    </Modal>
  );
}

ActorsToLayer.propTypes = {
  data: PropTypes.object,
  callback: PropTypes.func,
  visibility: PropTypes.bool,
  onClose: PropTypes.func,
  isSubmit: PropTypes.bool,
};

export default ActorsToLayer;
